import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** Kept in step with --ease-slow and --ease-menu in styles.css. */
export const DIALOG_MS = 240;
export const MENU_MS = 180;

/** Somebody who has asked their system for less movement gets none of this. */
function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Lets a thing finish leaving before it is taken off the page.
 *
 * WHY THIS IS NEEDED AT ALL
 *
 * Arriving is easy: the element is added, and a CSS animation runs. Leaving is
 * not, because by the time anything could animate, the element is already gone —
 * React has removed it. Nothing can animate what is not there.
 *
 * So closing happens in two steps. The first marks it as leaving, which starts
 * the animation; the second, once that has had time to run, actually closes it.
 * Every dialog and menu here uses this, so they all take exactly as long as
 * their animation does rather than each guessing.
 *
 * THE TIMER IS CANCELLED IF THE COMPONENT GOES FIRST
 *
 * Otherwise closing a card and immediately opening another would fire the old
 * timer part-way through the new one, shutting it as it appeared — the kind of
 * fault that looks random and is very hard to place.
 *
 * @param close what to run once it has finished leaving
 * @param ms    how long the leaving animation takes
 */
/**
 * Escape closes a dialog — the one keypress everybody reaches for to back out.
 *
 * Several dialogs answered Escape and several did not, which is worse than none
 * answering it: the key that shut one trapped you in the next. This is the one
 * behaviour they share, bound on the window (the same place IssueModal and the
 * budget popup already bind it) so it fires wherever focus happens to be.
 *
 * Pass the dialog's own close — usually `dismiss` from useDismiss, so the leave
 * animation still runs. `active` lets a dialog hold Escape back while an inner
 * layer owns it (a picker open over the dialog): pass false and that layer keeps
 * the key, exactly as the mention picker does on the card.
 *
 * NOT for a dialog that deliberately refuses casual dismissal — the ones that
 * ask a question a stray keystroke should not answer (WhyClosed, FinishSprint,
 * the terms). Those leave Escape unbound on purpose.
 */
export function useEscapeToClose(close: () => void, active: boolean = true) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, active]);
}

/** What Tab reaches: the selector for the trap, filtered further at read time. */
const FOCUSABLE = 'a[href],button,textarea,input,select,[tabindex]';

/**
 * Keeps keyboard focus inside a dialog, and hands it back when the dialog goes.
 *
 * WHY THIS IS NEEDED. A dialog that does not hold focus is a dialog only for the
 * mouse: Tab walks straight out of it and into the page behind, which the person
 * cannot see and may not know is there — so they type into, and act on, controls
 * that are supposed to be covered. `aria-modal` tells a screen reader the rest is
 * inert; nothing was making it true for the keyboard.
 *
 * WHAT IT DOES. On open it moves focus into the dialog (unless something inside
 * already has it — an autofocused field is left alone), Tab and Shift-Tab wrap
 * around the dialog's own focusable controls rather than leaving, and on close it
 * returns focus to whatever opened the dialog, so the keyboard lands back where
 * it was rather than at the top of the page.
 *
 * `ref` points at the dialog's own element (the panel, not the backdrop), which
 * needs tabIndex={-1} so it can hold focus when it has no controls of its own.
 * `active` is whether the dialog is open — pass it so the trap arms as the dialog
 * appears and releases (restoring focus) as it leaves.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean = true) {
  /*
   * WHO TO HAND FOCUS BACK TO, captured AS THE DIALOG OPENS — during the render
   * where `active` first turns true, before React has committed the dialog and
   * run any autoFocus. Captured in the effect instead, it would only ever be the
   * field the dialog autofocused (which is torn down on close), and focus would
   * fall to the page top. Reading activeElement here is a plain read of the DOM
   * as it still stands, the opener with focus and the dialog not yet mounted.
   */
  const opener = useRef<HTMLElement | null>(null);
  const armed = useRef(false);
  if (active && !armed.current) {
    // Guarded for server rendering (the screen tests render with no document).
    opener.current =
      typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    armed.current = true;
  } else if (!active && armed.current) {
    armed.current = false;
  }

  useEffect(() => {
    const root = ref.current;
    if (!active || !root) return;

    const focusables = () =>
      Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.hasAttribute('disabled') && el.tabIndex >= 0 && el.offsetParent !== null
      );

    // Move focus in — but never steal it from a field the dialog autofocused.
    if (!root.contains(document.activeElement)) root.focus();

    /*
     * Bound on the document in the capture phase, not on the panel, so that even
     * if focus has slipped outside — a menu or picker portaled to the body, or a
     * stray click behind — the next Tab pulls it back in rather than walking off
     * across the page. Only one dialog traps at a time, so this cannot fight
     * another trap.
     */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const here = document.activeElement as HTMLElement | null;
      if (!root.contains(here)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && here === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && here === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      // Hand focus back to whatever opened this, if it is still on the page.
      const back = opener.current;
      if (back && back !== document.body && document.contains(back)) back.focus();
    };
  }, [ref, active]);
}

export function useDismiss(close: () => void, ms: number = DIALOG_MS) {
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const dismiss = useCallback(() => {
    if (reducedMotion()) {
      close();
      return;
    }
    // Guarded, so a second Escape while it is already going does not queue a
    // second close — which on a menu would shut whatever opened after it.
    if (timer.current) return;
    setLeaving(true);
    timer.current = setTimeout(() => {
      // BOTH OF THESE HAVE TO BE PUT BACK, or this only works once.
      //
      // A dialog gets away with not doing it because it is taken off the page
      // and built again from nothing next time. A MENU IS NOT: the account menu
      // stays mounted for the life of the app and only toggles. So the raised
      // flag and the spent timer both survived the close — and reopening drew
      // the menu already marked as leaving, which played the closing animation
      // the instant it appeared. It flashed and went. Worse, the spent timer
      // made the guard above true for ever, so it could never be closed
      // properly again either.
      timer.current = undefined;
      setLeaving(false);
      close();
    }, ms);
  }, [close, ms]);

  return { leaving, dismiss };
}
